import type { V2Balance } from '../../../types/v2';
import { usdtValueOf } from '../../../utils/balanceOrder';
import { compareByNetwork } from '../../../utils/networkOrder';
import { isStockAccountBalance } from '../../../utils/xstocks';
import { COUNTER_SYMBOL } from './exchangeQuote';

export interface SellOption {
  key: string;
  symbol: string;
  networkSymbol: string;
  networkName: string;
  iconUrl: string | null;
  available: number;
  balance: V2Balance;
}

/**
 * Sell choices (spec 3.1): current-account balances on real wallets, plus
 * shares on xStocks accounts (one account per stock — the only place the
 * backend sells a stock from). An xStock left on a current account is not
 * offered: the order would be refused.
 *
 * Empty accounts are offered too, as the Withdraw picker offers them. They
 * used to be filtered out, and a client who had just registered opened
 * Exchange on "Select token" over an empty list — nothing to pick, and no
 * hint that the account exists and wants funding. An empty balance simply
 * fails validation, which is where that belongs.
 */
export const buildSellOptions = (
  balances: V2Balance[],
  xStockSymbols: ReadonlySet<string>,
): SellOption[] => {
  const opts: SellOption[] = balances
    .filter(
      (b) =>
        isStockAccountBalance(b)
        || ((b.account_type_code ?? 'current') === 'current'
          && b.provider_name !== 'virtual'
          && !xStockSymbols.has(b.token_symbol)),
    )
    .map((b) => ({
      key: `${b.token_symbol}|${b.network_symbol || 'no-net'}|${b.id}`,
      symbol: b.token_symbol,
      networkSymbol: b.network_symbol || '',
      networkName: b.network_name || '',
      iconUrl: b.icon_url ?? null,
      available: parseFloat(b.available_amount ?? b.amount ?? '0') || 0,
      balance: b,
    }));
  const tokenRank = (o: SellOption) =>
    (isStockAccountBalance(o.balance) ? 2 : o.symbol.toUpperCase() === 'USDT' ? 0 : 1);
  opts.sort((a, b) => {
    const ra = tokenRank(a);
    const rb = tokenRank(b);
    // Stocks after every crypto balance; crypto keeps its network order.
    if ((ra === 2) !== (rb === 2)) return ra - rb;
    const byNetwork = compareByNetwork(a, b);
    if (byNetwork !== 0) return byNetwork;
    if (ra !== rb) return ra - rb;
    return a.symbol.localeCompare(b.symbol);
  });
  return opts;
};

/**
 * Which option Sell opens on when nothing named one — the Dashboard's
 * Exchange button, or an account screen whose balance is empty.
 *
 * The biggest holding, priced in USDT, since that is what a client is most
 * likely to be spending. An account with something spendable beats one
 * whose funds are all locked, however much it is worth. On a new account
 * every balance is zero and they all tie, so what comes after decides:
 * USDT, then the TRON row — the account a client is given first, and the
 * one the form used to land on by accident of the network order.
 *
 * Shares are not offered: opening the screen on "sell your Apple" is no
 * way to greet someone, and the picker already lists stocks last. A client
 * who holds nothing else still gets one, rather than an empty field.
 */
export const defaultSellOption = (options: SellOption[]): SellOption | null => {
  const spendable = (o: SellOption) => (o.available > 0 ? 0 : 1);
  const isCounter = (o: SellOption) => (o.symbol.toUpperCase() === COUNTER_SYMBOL ? 0 : 1);
  const preferred = options.filter((o) => !isStockAccountBalance(o.balance));
  return [...(preferred.length > 0 ? preferred : options)].sort((a, b) => {
    if (spendable(a) !== spendable(b)) { return spendable(a) - spendable(b); }
    const byValue = usdtValueOf(b.balance) - usdtValueOf(a.balance);
    if (byValue !== 0) { return byValue; }
    if (isCounter(a) !== isCounter(b)) { return isCounter(a) - isCounter(b); }
    return compareByNetwork(a, b);
  })[0] ?? null;
};

/**
 * Exchange (limit order) — WAL-14.
 *
 * Sell and Buy are two different kinds of choice, and that asymmetry drives
 * the whole screen: Sell is one of the user's own current-account balances
 * (nil balances included — they simply fail validation), while Buy is any
 * active token from the catalogue, whether or not the user holds it.
 *
 * Amounts and price are kept as raw strings and only parsed for arithmetic.
 * Tokens are identified by row id rather than ticker: the catalogue holds
 * several rows per symbol (USDT on three networks, BTC with 6 and with 8
 * decimals), so the picked row — not the ticker — decides precision.
 */
